import { useContext, useEffect, useMemo, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { decryptData } from "../../../utils/encrypt";
import { ToastContext } from "../../../context/ToastContext";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import { useEmployeFilter } from "../../../context/EmployeFilterContext";

export const useNotification = () => {
    const { showSuccess } = useContext(ToastContext);
    const { setBadget, setTicketComment } = useEmployeFilter();
    const [logMessages, setLogMessages] = useState([]);
    const connectionRef = useRef(null);

    const isIOS = () => {
        if (typeof navigator === "undefined") return false;
        return /iPad|iPhone|iPod/.test(navigator.userAgent)
    };

    useEffect(() => {
        const log = (msg) => {
            setLogMessages((prev) => [...prev, msg]);
        };
        if (isIOS()) {
            log("iOS detected: skipping native Notification API use.");
        } else if (Notification.permission !== "granted") {
            Notification.requestPermission().then((permission) => {
                if (permission === "granted") {
                    log("Notification permission granted.");
                } if (Notification.permission === "denied") {
                    userBlocked()
                } else {
                    log("Notification permission denied or dismissed.");
                }

            });
        }
        if (sessionStorage.getItem("notification") === "true" && sessionStorage.getItem("token")) {
            const rawToken = sessionStorage.getItem("token");
            const decrypted = decryptData(rawToken);
            const token = decrypted?.replace(/^Bearer\s+/i, "");
            
            // Build query params to ensure authorization succeeds across all fallback transports (WebSockets, SSE, LongPolling)
            // even if the backend is expecting the token in a different query parameter or format.
            const queryParams = new URLSearchParams({
                access_token: token,
                token: token,
                Authorization: `Bearer ${token}`
            }).toString();

            const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
            const transportType = isLocalhost 
                ? signalR.HttpTransportType.LongPolling 
                : (signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling);

            const connection = new signalR.HubConnectionBuilder()
                .withUrl(`${process.env.REACT_APP_BASEURL}/srkay_insights_socket/WebSocket/notifications?${queryParams}`, {
                    accessTokenFactory: () => token,
                    transport: transportType
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Information)
                .build();

            connectionRef.current = connection;
            connection.on("ReceiveMessage", (data) => {
                log(data)
                if (isIOS()) {
                    log("iOS detected: skipping native Notification API use.");
                } else if (Notification.permission === "granted") {
                    notificationReceived(data?.notification_queue_id)
                    const notif = new Notification(data?.message_title, {
                        body: data?.message,
                        requireInteraction: true
                    });
                    setTimeout(() => {
                        notif.close();
                    }, 30000);
                    notif.onclick = () => {
                        window.focus();
                    };
                } else {
                    showSuccess('Sucess', data?.message)
                }
            });
            connection.on("ReceiveBadgeCount", (data) => {
                setBadget((prev) => ({ ...prev, [data?.badge_count[0]?.resource_key]: [data?.badge_count[0]] }))
                if (isIOS()) {
                    log("iOS detected: skipping native Notification API use.");
                }
            });
            connection.on("ReceiveEstimation", (data) => {
                const notif = new Notification(data?.message_title, {
                    body: data?.message,
                    requireInteraction: true
                });
                setTimeout(() => {
                    notif.close();
                }, 30000);
                notif.onclick = () => {
                    window.focus();
                };
            });
            connection.on("ReceiveComment", (comment) => {
                log(comment)
                setTicketComment(comment)
            });
            connection.onreconnecting(error => {
                log(`Connection lost due to error "${error}". Reconnecting...`);
            });

            connection.onreconnected(connectionId => {
                log(`Reconnected successfully. ConnectionId: ${connectionId}`);
            });

            connection.onclose(error => {
                log(`Connection closed. Error: ${error}`);
            });

            connection
                .start()
                .then(() => log("Connected....."))
                .catch((err) => log(" Connection Error: " + err));

            return () => {
                if (connection) {
                    connection.stop();
                }
            };
        }
    }, []);

    useEffect(() => {
        console.log("-", logMessages?.slice(-5));
    }, [logMessages]);


    const notificationReceived = async (id) => {
        try {
            const payload = {
                "notification_queue_id": id,
                "is_sent": true
            }
            await services.getInvokeRemote("POST", Urls("UPSERTSENTNOTIFICATION"), payload)
        } catch (error) {
            console.error(error)
        }
    }

    const userBlocked = async () => {
        try {
            const payload = {
                "requested_user_code": decryptData(sessionStorage.getItem("user_code"))
            }
            await services?.getInvokeRemote("POST", Urls("UPSERTNOTIFICATIONBLOCKEDBYUSER"), payload)
        } catch (error) {
            console.error(error)
        }
    }

    return useMemo(() => ({
        logMessages
    }), [logMessages]);
};